import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Settings, Trash2, Copy, GitBranch, Clock, CheckCircle } from 'lucide-react';
import { useAppStore } from '../store/useAppStore';
import { getProcesses, deleteProcess, duplicateProcess } from '../services/processService';
import { ProfileIcon, ProfileMenu, ProfileMenuItem } from '../components/Header/ProfileMenu';
import { FaUserCircle } from 'react-icons/fa';
import { IoSettingsOutline } from 'react-icons/io5';
import { FiLogOut } from 'react-icons/fi';
import Logout from '../components/Logout/Logut';
import useProfileStore from '../store/useProfileStore';
import logo from '../assets/images/newLogo.png';

const ProcessDashboard = () => {
  const { appName } = useParams(); // Extract appName from URL path
  const { setAppName } = useAppStore();
  const navigate = useNavigate();
  const userImage = useProfileStore((state) => state.userImage);
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    console.log('📂 [ProcessDashboard] appName from URL:', appName);
    if (appName) {
      setAppName(appName); // Set appName in store for other components
      fetchProcesses();
    } else {
      console.error('❌ [ProcessDashboard] No appName in URL - redirecting');
      navigate('/dashboard');
    }
  }, [appName, setAppName, navigate]);

  const fetchProcesses = async () => {
    try {
      setLoading(true);
      const data = await getProcesses(appName);
      setProcesses(data);
    } catch (error) {
      console.error('Error loading processes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProcess = () => {
    navigate(`/dashboard/${appName}/process-builder/new`);
  };

  const handleEditProcess = (processId) => {
    navigate(`/dashboard/${appName}/process-builder/${processId}`);
  };

  const handleDuplicateProcess = async (processId, e) => {
    e.stopPropagation();
    try {
      await duplicateProcess(appName, processId);
      fetchProcesses();
    } catch (error) {
      console.error('Error duplicating process:', error);
      alert('Failed to duplicate process');
    }
  };

  const handleDeleteProcess = async (processId) => {
    try {
      await deleteProcess(appName, processId);
      setDeleteConfirm(null);
      fetchProcesses();
    } catch (error) {
      console.error('Error deleting process:', error);
      alert('Failed to delete process');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Custom Navigation Bar */}
      <div style={{
        padding: '12px 24px',
        background: '#050618',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        minHeight: '5.25rem',
        position: 'sticky',
        top: 0,
        zIndex: 1000
      }}>
        {/* Left Section: MUDUMBAI Logo */}
        <div 
          style={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            marginRight: '32px'
          }}
          onClick={() => navigate('/')}
        >
          <img 
            src={logo} 
            alt="MUDUMBAI Logo" 
            style={{
              height: '3.5rem',
              width: 'auto',
              objectFit: 'contain'
            }}
          />
        </div>

        {/* Center Section: Page-specific content */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          flex: 1
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              background: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px'
            }}>
              🔄
            </div>
            <div>
              <h1 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: '700',
                color: '#FFFFFF',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
              }}>
                Process Workflows
              </h1>
              <p style={{
                margin: 0,
                fontSize: '12px',
                color: 'rgba(255, 255, 255, 0.8)'
              }}>
                Design simple, multi-step workflows for {appName}
              </p>
            </div>
          </div>
        </div>

        {/* Right Section: Page-specific buttons, Go to Dashboard, and Profile */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          {/* New Process Button */}
          <button
            onClick={handleCreateProcess}
            style={{
              padding: '8px 14px',
              background: 'rgba(37, 99, 235, 0.8)',
              border: '1px solid rgba(37, 99, 235, 0.5)',
              borderRadius: '8px',
              color: '#FFFFFF',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(37, 99, 235, 1)';
              e.target.style.borderColor = 'rgba(37, 99, 235, 0.8)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(37, 99, 235, 0.8)';
              e.target.style.borderColor = 'rgba(37, 99, 235, 0.5)';
            }}
          >
            <Plus size={16} />
            <span>New Process</span>
          </button>

          {/* Go to Dashboard Button */}
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              padding: '8px 14px',
              background: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '8px',
              color: '#FFFFFF',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.3)';
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.2)';
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
            }}
          >
            <span>🏠</span>
            <span>Go to Dashboard</span>
          </button>

          {/* Profile Icon */}
          <div style={{ marginLeft: '12px' }}>
            <ProfileMenu
              trigger={
                <div style={{
                  width: '2.75rem',
                  height: '2.75rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                }}
                >
                  <ProfileIcon userImage={userImage} />
                </div>
              }
            >
              <ProfileMenuItem onClick={() => navigate('/userprofile')}>
                <FaUserCircle size={22} /> Profile
              </ProfileMenuItem>
              <ProfileMenuItem onClick={() => navigate('/settings')}>
                <IoSettingsOutline size={22} /> Setting
              </ProfileMenuItem>
              <ProfileMenuItem>
                <FiLogOut size={20} /> <Logout />
              </ProfileMenuItem>
            </ProfileMenu>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
          </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl">
                <GitBranch size={24} className="text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600 font-medium">Total Processes</p>
                <p className="text-3xl font-bold text-gray-900">{processes.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-br from-green-100 to-green-200 rounded-xl">
                <CheckCircle size={24} className="text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600 font-medium">Active</p>
                <p className="text-3xl font-bold text-gray-900">
                  {processes.filter(p => p.status === 'active').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-br from-orange-100 to-orange-200 rounded-xl">
                <Clock size={24} className="text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600 font-medium">Draft</p>
                <p className="text-3xl font-bold text-gray-900">
                  {processes.filter(p => !p.status || p.status === 'draft').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Process List */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Loading processes...</p>
          </div>
        ) : processes.length === 0 ? (
          <div className="bg-white rounded-xl border-2 border-dashed border-gray-300 p-12 text-center">
            <GitBranch size={48} className="mx-auto mb-4 text-gray-400" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No processes yet</h3>
            <p className="text-gray-600 mb-6">
              Create your first process workflow to get started
            </p>
            <button
              onClick={handleCreateProcess}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition inline-flex items-center gap-2"
            >
              <Plus size={20} />
              Create Process
            </button>
          </div>
        ) : (
          <div className="grid gap-4">
            {processes.map(process => (
              <div
                key={process.id}
                className="bg-white rounded-xl shadow-sm p-6 hover:shadow-lg transition cursor-pointer border-l-4 border-blue-500"
                onClick={() => handleEditProcess(process.id)}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-semibold text-gray-900">{process.name}</h3>
                      {process.status === 'active' && (
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                          Active
                        </span>
                      )}
                      {(!process.status || process.status === 'draft') && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded">
                          Draft
                        </span>
                      )}
                    </div>
                    
                    <p className="text-gray-600 mb-4">{process.description || 'No description'}</p>
                    
                    <div className="flex items-center gap-6 text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <GitBranch size={16} />
                        <span>{process.steps?.length || 0} steps</span>
                      </div>
                      
                      {process.updatedAt && (
                        <div className="flex items-center gap-1">
                          <Clock size={16} />
                          <span>
                            Updated {new Date(process.updatedAt).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                      
                      {process.permissions && (
                        <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded">
                          {process.permissions.type || 'Public'}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={(e) => handleDuplicateProcess(process.id, e)}
                      className="p-2 hover:bg-blue-50 rounded-lg transition text-blue-600"
                      title="Duplicate Process"
                    >
                      <Copy size={18} />
                    </button>
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditProcess(process.id);
                      }}
                      className="p-2 hover:bg-gray-100 rounded-lg transition"
                      title="Edit Process"
                    >
                      <Settings size={18} />
                    </button>
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConfirm(process.id);
                      }}
                      className="p-2 hover:bg-red-50 rounded-lg transition text-red-600"
                      title="Delete Process"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Process?</h3>
              <p className="text-gray-600 mb-6">
                This action cannot be undone. The process and all its configuration will be permanently deleted.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteProcess(deleteConfirm)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                >
                  Delete Process
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default ProcessDashboard;

